// src/screens/JobPaste/index.tsx
import SearchIcon from 'assets/icons/searchIconpps.svg';
import CollaborativeEditors from 'components/CollaborativeEditors/CollaborativeEditors';
import Navbar from 'components/navbar';
import { usePpsAccess } from 'hooks/usePpsAccess';
import { useStore } from 'hooks/useStore';
import { CalendarCheck, CirclePlus, Filter } from 'lucide-react';
import { observer } from 'mobx-react';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { useEffect } from 'react';
import { statusCodesStore } from 'stores/ReferenceStore';
import AddJobModal from './components/AddJobModal/AddJobModal';
import BulkJobUpload from './components/bulkJobUploadModal/BulkJobUpload';
import ImportSysproJobsModal from './components/ImportSysproJobsModal/ImportSysproJobsModal';
import JobPasteSkeleton from './components/JobPasteSkeleton/JobPasteSkeleton';
import ChangesRevertedWebSocketHandler from './components/ChangesRevertedWebSocketHandler';
import FieldValueWebSocketHandler from './components/FieldValueWebSocketHandler';
import JobReorderWebSocketHandler from './components/JobReorderWebSocketHandler';
import OverrideCaptureModal from './components/OverrideCaptureModal';
import RoomAccordionWithWebSocket from './components/RoomAccordion/RoomAccordionWithWebSocket';
import ScheduleAttainment from './components/ScheduleAttainmentModal';
import classes from './jobpaste.module.scss';
import { useJobPaste } from './useJobPaste';

const JobPaste = () => {
  const {
    searchValue,
    setSearchValue,
    loading,
    setShift,
    setDate,
    shift,
    date,
    handleModalPOpen,
    handleScheduleAttainmnetModalOpen,
    scheduleOptions,
    shiftTimingStore,
    ScheduleAttainmentStore,
    sendEditingStatus,
    sendActiveUserUpdate,
    isConnected
  } = useJobPaste();

  const { UserStore, JobPasteStore } = useStore();
  // Use the usePpsAccess hook to get permission functions
  const { canAddJobs, canViewScheduleAttainment, canViewSysproJobs, canEditSharedFilter } =
    usePpsAccess();

  useEffect(() => {
    if (UserStore.userDepartment) {
      statusCodesStore.fetchPossibleStatusTransitions(UserStore.userDepartment);
    }
  }, [UserStore.userDepartment, statusCodesStore]);

  // Create anonymous user on component mount
  // useEffect(() => {
  //   const userEmail = UserStore.userMetadata?.userInfo.email;
  //   if (userEmail) {
  //     const userName = setUserNameFromOcta(userEmail);
  //   }
  // }, []);

  return (
    <div className={classes.container}>
      <Navbar heading="PPS Scheduling Tool Enhancement" />
      <div className={classes.downSection}>
        <div className={classes.filtersAndSearching}>
          <div className={classes.dateAndShiftRange}>
            <div className={classes.shift}>
              <span className={classes.inputLabel}>Shift</span>
              <Dropdown
                loading={shiftTimingStore.isLoading}
                disabled={!canEditSharedFilter() || JobPasteStore.savingAllRooms}
                value={shift}
                options={scheduleOptions}
                optionLabel="label"
                optionValue="code"
                onChange={e => setShift(e.value)}
                placeholder={`${shiftTimingStore.isLoading ? 'loading...' : 'Select Shift'}`}
              />
            </div>
            <div className={classes.date}>
              <span className={classes.inputLabel}>Date</span>
              <Calendar
                className={classes.calender}
                disabled={!canEditSharedFilter() || JobPasteStore.savingAllRooms}
                value={date}
                onChange={e => setDate(e.target.value as Date)}
                dateFormat="mm-dd-yy"
                showIcon={true}
              />
            </div>

            {/* add confirmation apply filter button to set the filter */}

            {canEditSharedFilter() && (
              <Button
                label="Apply Date and Shift"
                onClick={() => {
                  JobPasteStore.applyFilterAndSaveAllRooms();
                }}
                disabled={JobPasteStore.savingAllRooms}
                loading={JobPasteStore.savingAllRooms}
                size="small"
                style={{
                  backgroundColor: '#4d2e6d',
                  borderColor: '#4d2e6d',
                  color: 'white',
                  fontSize: '14px',
                  padding: '0.5rem 1rem'
                }}
                icon={
                  !JobPasteStore.savingAllRooms ? <Filter className={classes.addIcon} /> : undefined
                }
              />
            )}
          </div>
          <div className={classes.searchComponent}>
            <Button
              outlined
              data-testid="drugSearchButton"
              className={classes.searchIconButton}
              onClick={() => { }}
              disabled={false}
            >
              <img className={classes.searchIcon} src={SearchIcon} alt="search icon" />
            </Button>
            <Divider className={classes.divider} layout="vertical" />
            <div className={classes.searchBarRightSide}>
              <AutoComplete
                autoFocus
                minLength={1}
                delay={100}
                autoHighlight={true}
                disabled={false}
                onKeyDown={() => { }}
                placeholder="Search jobs by ID, description, type, NDC..."
                style={{ flexGrow: 1 }}
                className={'searchField'}
                value={searchValue}
                dropdownIcon={null}
                onChange={e => setSearchValue(e.value)}
              />
            </div>
          </div>
          <div className={classes.filter}>
            {/* Only show Schedule Attainment button if user has permission */}
            {canViewScheduleAttainment() && (
              <Button
                onClick={handleScheduleAttainmnetModalOpen}
                style={{ paddingRight: '10px' }}
                label="Schedule Planner"
                className={classes.filterButton}
                icon={<CalendarCheck className={classes.addIcon} />}
                loading={shiftTimingStore.isLoading || ScheduleAttainmentStore.isLoading}
              />
            )}

            {/* Only show Add Job button if user has permission */}
            {canAddJobs() && (
              <Button
                onClick={handleModalPOpen}
                style={{ paddingRight: '10px' }}
                label="Add a job"
                className={classes.filterButton}
                icon={<CirclePlus className={classes.addIcon} />}
              />
            )}
          </div>
        </div>

        {/* Collaborative Editors Panel - Full Width */}
        <div className={classes.collaborativeSection}>
          <CollaborativeEditors />
        </div>
        <div className={classes.roomData}>
          {loading ? (
            <JobPasteSkeleton />
          ) : (
            <div className={classes.jobscontainer}>
              <RoomAccordionWithWebSocket
                sendEditingStatus={sendEditingStatus}
                sendActiveUserUpdate={sendActiveUserUpdate}
                isConnected={isConnected}
              />
            </div>
          )}
        </div>
      </div>
      <AddJobModal />
      <OverrideCaptureModal sendActiveUserUpdate={sendActiveUserUpdate} isConnected={isConnected} />
      {canViewSysproJobs() && <BulkJobUpload />}
      <ScheduleAttainment />
      {canViewSysproJobs() && <ImportSysproJobsModal />}
      <JobReorderWebSocketHandler />
      <FieldValueWebSocketHandler />
      <ChangesRevertedWebSocketHandler />
    </div>
  );
};

export default observer(JobPaste);
